import { memo, useCallback, useState } from 'react';

import { Close, Pause, Play, TriangleDown, TriangleRight } from '@air/next-icons';

import { FileStatus } from '../../constants/FileStatus';
import { FileStatusButton } from './FileStatusButton';

export type FileStatusPanelHeaderButtonsProps = {
  onResume?: () => Promise<void> | void;
  onCancel?: () => void;
  onPause?: () => void;
  onDismiss?: () => void;
  status?: FileStatus;
  isUploaderOpen: boolean;
  onToggle?: (isOpen: boolean) => void;
  disableCancel?: boolean;
  disableDismiss?: boolean;
};

export const FileStatusPanelHeaderButtons = memo(
  ({
    onDismiss,
    onPause,
    onResume,
    status,
    onCancel,
    isUploaderOpen,
    onToggle,
    disableCancel,
    disableDismiss,
  }: FileStatusPanelHeaderButtonsProps) => {
    const [isResuming, setIsResuming] = useState(false);

    const handleResume = useCallback(async () => {
      setIsResuming(true);
      try {
        await onResume?.();
      } finally {
        setIsResuming(false);
      }
    }, [onResume]);

    return (
      <>
        {onPause && status === FileStatus.progressing && (
          <FileStatusButton label="Pause" onClick={onPause}>
            <Pause className="size-4" />
          </FileStatusButton>
        )}

        {onResume && (status === FileStatus.stopped || status === FileStatus.paused) && (
          <FileStatusButton isLoading={isResuming} label="Resume" onClick={handleResume}>
            <Play className="size-4" />
          </FileStatusButton>
        )}

        {onDismiss && !disableDismiss && (disableCancel || status === FileStatus.completed) && (
          <FileStatusButton data-testid="CLOSE_UPLOADS_BUTTON" label="Close" onClick={onDismiss}>
            <Close className="size-4" />
          </FileStatusButton>
        )}

        {onCancel && status !== FileStatus.completed && (
          <FileStatusButton data-testid="CANCEL_UPLOADS_BUTTON" label="Cancel" onClick={onCancel}>
            <Close className="size-4" />
          </FileStatusButton>
        )}
        {onToggle &&
          (isUploaderOpen ? (
            <FileStatusButton appearance="ghost" label="Minimize" onClick={() => onToggle(false)}>
              <TriangleDown className="size-4" />
            </FileStatusButton>
          ) : (
            <FileStatusButton appearance="ghost" label="Expand" onClick={() => onToggle(true)}>
              <TriangleRight className="size-4" />
            </FileStatusButton>
          ))}
      </>
    );
  },
);

FileStatusPanelHeaderButtons.displayName = 'FileStatusPanelHeaderButtons';
